import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useParams, useNavigate } from 'react-router-dom';
import Errors from '../common/Errors';
import Icon from '../common/Icon';
import { DividedRow, Full, Half, Quarter } from '../common/RowLengths';
import Accordion from '../common/Accordion';
import slugify from '../../helpers/slugify';
import { handleCreateForm } from './constants/handleForm';

const NewForm = ({ updateFormIndex }) => {
  const params = useParams();
  const navigate = useNavigate();
  const [showNewForm, setShowNewForm] = useState(false);
  const initial = {
    display_name: '',
    name: '',
    slug: '',
    description: '',
    completion_text: '',
    global_quota: '',
    active: true,
  };
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getFieldState,
    reset,
    setError,
    formState: { errors, isSubmitSuccessful },
  } = useForm({
    criteriaMode: 'all',
    defaultValues: initial,
  });

  useEffect(() => {
    if (
      document.activeElement.name == 'name' &&
      getFieldState('name').isDirty
    ) {
      setValue('slug', slugify(watch('name')), { shouldDirty: true });
    }
  }, [watch('name')]);

  useEffect(() => {
    setShowNewForm(false);
    reset(initial);
  }, [isSubmitSuccessful]);

  const toggleShowNewForm = (e) => {
    e.preventDefault();
    if (showNewForm) {
      reset();
      document.activeElement.blur();
    }
    setShowNewForm(!showNewForm);
  };

  const onSubmit = async (formData) => {
    const container_id = params.container_id;
    let newForm = await handleCreateForm(formData, container_id);
    if (newForm.ok) {
      updateFormIndex(newForm.data);
      navigate(`/manage/forms/${newForm.data.id}/edit`);
    } else if (newForm.errors) {
      Object.entries(newForm.errors).forEach(([key, message]) =>
        setError(key, { types: { [key]: message } })
      );
    }
  };

  return (
    <Accordion
      id='new-form-dropdown'
      expanded={showNewForm}
      toggle={toggleShowNewForm}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset>
          <DividedRow>
            <Full title='Display Name:'>
              <input
                placeholder='Participant Registration'
                {...register('display_name')}
              />
            </Full>
          </DividedRow>

          <DividedRow>
            <Half title='Name:'>
              <input
                id='form-name'
                placeholder='Registration'
                {...register('name', {
                  required: 'Required',
                  deps: 'slug',
                })}
              />
              <ErrorMessage
                errors={errors}
                name='name'
                render={({ messages }) => <Errors messages={messages} />}
              />
            </Half>

            <Half title='Slug:'>
              <input
                id='form-slug'
                placeholder='registration'
                {...register('slug', {
                  required: 'Required',
                  onBlur: (e) => setValue('slug', slugify(e.target.value)),
                })}
              />
              <ErrorMessage
                errors={errors}
                name='slug'
                render={({ messages }) => <Errors messages={messages} />}
              />
            </Half>
          </DividedRow>

          <DividedRow>
            <Full title='Description:'>
              <textarea
                id='form-description'
                {...register('description')}
                placeholder='e.g. This form is **awesome**!'
              />
            </Full>
          </DividedRow>

          <DividedRow>
            <Full title='Completion Text:'>
              <textarea
                id='form-completion'
                {...register('completion_text')}
                placeholder='e.g. Thanks for your submission...'
              />
            </Full>
          </DividedRow>

          <DividedRow>
            <Half title='Global Quota:'>
              <input
                id='form-global-quota'
                type='number'
                min={0}
                {...register('global_quota', {
                  setValueAs: (v) => (v == 0 ? null : v),
                })}
              />
            </Half>
          </DividedRow>

          <DividedRow>
            <Quarter hasCheckbox labelFor='form-active' title='Active?'>
              <input type='checkbox' id='form-active' {...register('active')} />
            </Quarter>
          </DividedRow>
        </fieldset>

        <div className='controls'>
          <button type='submit'>
            Save
            <Icon icon='save' />
          </button>
          <button className='blue' onClick={toggleShowNewForm}>
            Cancel
            <Icon icon='xmark' />
          </button>
        </div>
      </form>
    </Accordion>
  );
};

export default NewForm;
